import { useCallback, useContext, useMemo } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { Loader } from '../Common/Loader';
import { useTranslate } from '@/hooks/useTranslate';
import { cn } from '@/utils/cn';
import { trackPayButtonBlockedClick } from '@/utils/conversionTracking';
import { PaymentMethodEnum } from '@/types/common';
import { BRANDED_PAY_CTA_CONFIG } from './brandedPayCta.config';

interface BrandedPayCTAProps {
  /**
   * Which payment method this CTA represents. Determines the visual
   * (background color, wordmark) via {@link BRANDED_PAY_CTA_CONFIG}.
   * The caller is responsible for ONLY passing a method that has a
   * config entry — otherwise this component returns `null` as a safe
   * fallback (call sites should already gate on {@link hasBrandedPayCta}).
   */
  paymentMethod: PaymentMethodEnum;
  onClick: () => void;
  isDisabled?: boolean;
}

/**
 * Shared button chrome for APM-branded pay CTAs (BLIK, PIX, …) rendered
 * in PREFERRED-style templates where the primary CTA is the selected
 * payment method itself.
 *
 * Interaction contract vs. {@link PayButton}:
 *   - `data-testid="pay-button"` so existing E2E selectors keep working
 *     (payment CTA is a stable role, not a brand-specific one).
 *   - `data-brand={paymentMethod}` for finer targeting when tests need
 *     to assert the branded variant specifically.
 *   - Disabled/loader states match `PayButton`. The idle-pulse animation
 *     is intentionally NOT wired here — brand-locked CTAs (BLIK black,
 *     PIX teal) already read as the primary action on their own, and a
 *     wobbling wallet mark reads as broken rather than inviting.
 *
 * Kept intentionally dumb about payment orchestration: the parent form
 * owns `handlePayment` and passes it as `onClick`. No `runPayment`
 * fallback here (unlike `PayButton`) because branded CTAs always live
 * next to a form that needs bespoke pre-submit work (BLIK code, PIX
 * fields via PaymentElement).
 */
export const BrandedPayCTA = ({ paymentMethod, onClick, isDisabled }: BrandedPayCTAProps) => {
  const context = useContext(CheckoutContext);
  const {
    payment: { isInPaymentProcess },
  } = context;
  const { t } = useTranslate();

  const visual = BRANDED_PAY_CTA_CONFIG[paymentMethod];

  const isButtonDisabled = useMemo(() => {
    return isDisabled || isInPaymentProcess;
  }, [isDisabled, isInPaymentProcess]);

  const handleClick = useCallback(() => {
    if (!isButtonDisabled) {
      onClick();
      return;
    }
    // Mirrors PayButton: record a blocked-tap event when the shopper
    // clicked but the form gate rejected the intent (invalid state,
    // not an in-flight payment).
    if (!isInPaymentProcess) {
      trackPayButtonBlockedClick(context);
    }
  }, [context, isButtonDisabled, isInPaymentProcess, onClick]);

  if (!visual) {
    // Defensive: caller should have gated on hasBrandedPayCta. Silently
    // rendering nothing avoids painting a broken button; the parent
    // form must supply a fallback (PayButton) upstream if this happens.
    return null;
  }

  return (
    <div
      data-testid="pay-button"
      data-brand={paymentMethod}
      style={{
        backgroundColor: visual.backgroundColor,
        color: visual.foregroundColor,
      }}
      className={cn(
        'w-full h-12 rounded-md flex items-center justify-center p-spacing-2 gap-2 cursor-pointer',
        isButtonDisabled && 'opacity-50 cursor-not-allowed'
      )}
      onClick={handleClick}
    >
      {isInPaymentProcess ? (
        <Loader size="xs" color="white" />
      ) : (
        <>
          <span className="font-medium text-lg">{t(visual.labelKey ?? 'payWith')}</span>
          <span className="flex items-center" data-testid="branded-cta-wordmark">
            {visual.wordmark}
          </span>
        </>
      )}
    </div>
  );
};
